
import PageLayout from '@/components/PageLayout';
import { Mail, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { sendEmailNotification } from '@/utils/emailUtils';
import { useToast } from '@/hooks/use-toast';
import { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@radix-ui/react-label';
import { Input } from '@/components/ui/input';

const Partners = () => {
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const [refreshVersion, setRefreshVersion] = useState(Date.now());
  const mountedRef = useRef(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState('');
  const [email, setEmail] = useState('');
  
  useEffect(() => {
    console.log(`Partners page loaded - ensuring fresh content (mobile: ${isMobile})`);

    const timestamp = Date.now();
    setRefreshVersion(timestamp);
    
    document.documentElement.setAttribute('data-partners-version', timestamp.toString());
    
    const refreshImages = () => {
      console.log(`Refreshing partner images at ${timestamp}`);
      document.querySelectorAll('img').forEach(img => {
        if (img.src && !img.src.includes('data:')) {
          const currentSrc = img.src.split('?')[0];
          img.src = `${currentSrc}?v=${timestamp}&device=${isMobile ? 'mobile' : 'desktop'}`;
        }
      });
    };

    refreshImages();
    
    if (!mountedRef.current) {
      mountedRef.current = true;
      
      const refreshTimers = [100, 300, 1000].map(delay => 
        setTimeout(refreshImages, delay)
      );
      
      return () => {
        refreshTimers.forEach(timer => clearTimeout(timer));
      };
    }
  }, [isMobile]);
  
  const handleContactClick = (partnerName: string) => {
    setSelectedPartner(partnerName);
    setIsDialogOpen(true);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Error",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    try {
      await sendEmailNotification('Partner Contact Request', {
        partner: selectedPartner,
        timestamp: new Date().toISOString(),
        requestOrigin: 'Partners page'
      }, email);

      toast({
        title: "Request Sent",
        description: "Your contact request has been sent successfully."
      });
      
      setIsDialogOpen(false);
      setEmail('');
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send contact request. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <PageLayout>
      <main className="pt-12 sm:pt-16" key={`partners-main-${refreshVersion}`}>
        <section className="py-10 sm:py-12 md:py-16 relative overflow-hidden grid-bg" data-refresh-key={refreshVersion}>
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-3xl xs:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">Managing Partners</h1>
              <p className="text-base xs:text-lg sm:text-xl text-delphi-light/70 mb-6 sm:mb-8">
                The co-founders met in 2017 while working at Harvard. Immediately becoming friends, they bonded over the sentiment that physics research is too confined by the never ending grant process. Knowing the only way to research as they pleased would be to fund it themselves, the hedge fund industry was the clear mechanism to bridge their skills to their end goal. After years of collaboration and growth the possibility has never been closer, with DEL(PHI) offering a near perfect marriage of theoretical physics and dependable funding. Once fully funded, DEL(PHI) will serve as a crucible for pure-theory research that offers monetary incentive to capital partners without strings attached to the research itself.
              </p>
            </div>
          </div>
        </section>
        
        <section className="py-6 sm:py-8 md:py-12" data-refresh-key={refreshVersion}>
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="space-y-6 sm:space-y-8 md:space-y-12">
                <div className="data-section p-4 sm:p-6 md:p-8 bg-gradient-to-br from-delphi-gray/20 to-transparent backdrop-blur-sm">
                  <div className="flex flex-col md:flex-row gap-4 sm:gap-6">
                    <div className="w-full md:w-1/3">
                      <div className="w-full h-full aspect-square md:aspect-auto bg-gradient-to-br from-delphi-gray/10 to-transparent rounded-md overflow-hidden">
                        <div className="w-full relative" style={{ height: '100%', overflow: 'hidden' }}> 
                          <img 
                            src={`/lovable-uploads/115b0274-82e7-436d-a480-977cd2f65ed1.png?v=${refreshVersion}`} 
                            alt="Carson King" 
                            className="absolute top-0 left-0 w-full h-full object-cover object-top" 
                            style={{ transform: 'scale(1.005)', objectPosition: 'center top' }}
                            data-timestamp={refreshVersion}
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="md:w-2/3">
                      <div className="h-3 w-12 bg-delphi-red/70 mb-3 sm:mb-4"></div>
                      <h3 className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2">Carson King</h3>
                      <p className="text-delphi-red mb-3 sm:mb-4">Co-Founder</p>
                      
                      <p className="text-sm sm:text-base text-delphi-light/70 mb-4 sm:mb-6">Carson is a high-stakes problem solver. A first generation college student from the hills of Appalachia, he graduated Summa Cum Laude with a degree in Physics. Completing research projects at the Harvard-Smithsonian Center for Astrophysics, the NASA Goddard Space Flight Center, and JAXA (Japanese Space Program) he specialized in developing magnetohydrodynamic imaging algorithms aboard in-orbit satellites. Initially applying his algorithms to cash flow between asset classes, he founded several incubator funds to develop robust quant trading strategies. Managing a buying power of up to $1.2 million over the past several years, he specializes in high-risk, high-reward scenarios. His work on Emergent Dynamics of Cumulative Observations underpins the trading strategy that powers DEL(PHI).</p>
                      
                      <button onClick={() => handleContactClick('Carson King')} className="text-delphi-red hover:underline flex items-center">
                        <Mail size={16} className="mr-2" />
                        <span>Contact</span>
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="data-section p-4 sm:p-6 md:p-8 bg-gradient-to-br from-delphi-gray/20 to-transparent backdrop-blur-sm">
                  <div className="flex flex-col md:flex-row gap-4 sm:gap-6">
                    <div className="w-full md:w-1/3">
                      <div className="w-full h-full aspect-square md:aspect-auto bg-gradient-to-br from-delphi-gray/10 to-transparent rounded-md overflow-hidden">
                        <div className="w-full relative" style={{ height: '100%', overflow: 'hidden' }}> 
                          <img 
                            src={`/lovable-uploads/228c76f2-25fc-4950-8046-96ff988c292d.png?v=${refreshVersion}`} 
                            alt="Marcus DuPont" 
                            className="absolute top-0 left-0 w-full h-full object-cover object-top" 
                            style={{ transform: 'scale(1.005)', objectPosition: 'center top' }}
                            data-timestamp={refreshVersion}
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="md:w-2/3">
                      <div className="h-3 w-12 bg-delphi-red/70 mb-3 sm:mb-4"></div>
                      <h3 className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2">Marcus DuPont, Ph.D.</h3>
                      <p className="text-delphi-red mb-3 sm:mb-4">Co-Founder</p>
                      
                      <p className="text-sm sm:text-base text-delphi-light/70 mb-4 sm:mb-6">
                        Marcus is a trailblazing physicist with a remarkable journey. Growing up in a single-parent Haitian immigrant household, he overcame numerous challenges to become the first Black person to earn a PhD in Physics at New York University (NYU). Now a distinguished researcher, Marcus holds two prestigious prize fellowships at Princeton University, where he combines his expertise in astrophysics with groundbreaking interdisciplinary approaches including quantum mechanics and fundamental physics. Marcus' work has earned him recognition in academia and beyond, including authorship of over six peer-reviewed publications.
                      </p>
                      
                      <button onClick={() => handleContactClick('Marcus DuPont')} className="text-delphi-red hover:underline flex items-center">
                        <Mail size={16} className="mr-2" />
                        <span>Contact</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        <section className="py-12 sm:py-16 relative" data-refresh-key={refreshVersion}>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl xs:text-3xl font-bold mb-4 sm:mb-6">Interested in Joining Our Team?</h2>
              <p className="text-base sm:text-lg text-delphi-light/70 mb-6 sm:mb-10">
                DEL(PHI) is always seeking exceptional individuals with backgrounds in theoretical physics, mathematics, or quantitative finance who are passionate about pushing boundaries.
              </p>
              
              <Link to="/contact" className="px-5 py-2.5 sm:px-6 sm:py-3 bg-delphi-red text-white rounded-md font-medium inline-flex items-center gap-2 hover:bg-red-600 transition-colors">
                <span>Contact Us</span>
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      </main>
      
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[425px] bg-delphi-dark border-delphi-gray/20">
          <DialogTitle className="text-delphi-light">Enter Your Email</DialogTitle>
          <DialogDescription className="text-delphi-light/70">
            Please provide your email address so we can respond to your inquiry about {selectedPartner}.
          </DialogDescription>
          
          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-delphi-light">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="bg-delphi-dark/50 border-delphi-gray/40 text-delphi-light"
                required
              />
            </div>
            
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
              <Button
                type="submit"
                className="w-full bg-delphi-red hover:bg-red-600 text-white"
              >
                Submit Request
                <ArrowRight size={16} />
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default Partners;
